import clsx from "clsx";
import { ArrowUpRight, BarChart3, Calculator, FileText, Layers, List, Lock, Plus, Search, Tag, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { basisLabel } from "../../shared/money";
import { TextField, ToggleField } from "../../web/components/fields";
import { LeaveGuard } from "../../web/components/LeaveGuard";
import { RecordWindow } from "../../web/components/RecordWindow";
import { SyncBadge } from "../../web/components/SyncBadge";
import { AddPicker } from "../../web/components/AddPicker";
import { WindowPopup } from "../../web/components/WindowPopup";
import { matchesSearch, NeedsAttention } from "./ListToolbar";
import { attentionText, groupAttention, type ItemProblem, tintsRow } from "./attention";
import { InlinePicker } from "../../web/components/InlinePicker";
import { useConfirm, useWindowConfirm } from "../../web/components/Confirm";
import { Badge, Box, DraftBar, ErrorNotice, IssueChip, Issues, Notice, Spinner } from "../../web/components/ui";
import { lineDiff } from "../../web/lib/diff";
import { useMoney } from "../../web/lib/money";
import { can, freeGroupName, groupTaxBasis, itemName, itemTax, membersOf, outOfDate, type RowUpdate, updateEach, useGroupJobTime, usePriceListSaver, useRowUpdates, useStillShown, whyNot } from "./data";
import { type GroupTax, type ItemPricing, type MaterialRow, meta, type PriceListData, pricedByGroup, priceLock, type PricingGroupRow, type PricingRule } from "./meta";
import { Link } from "wouter";
import { describePricing } from "./describe";
import { groupLockAction, LockMark } from "./PriceLock";
import { type GroupTags, groupTagsName, type NameTagConfig, ownKeys, tagName } from "./names";
import { DiffLine, GroupTagFields, ServiceM8Name } from "./NameTags";
import { calcPrice, keepShownAmounts, missingCost, type NeedsCheck, needsCheck, partItem } from "./pricing";
import { jobTimeShown, PricingEditor } from "./PricingEditor";
import { type GroupDraft, GroupPricesToUpdate, GroupSave, itemCount, PricingCard, sameDraft, useGroupUpdateConfirm } from "./PricingTab";
import { GroupStatsTab, withStatsTab } from "./StatsTab";
import { useLeftUnsaved, usePriceListWindows, useUnsavedGuard } from "./windows";
import { DuplicatePopup, duplicateAction } from "./DuplicatePopup";

/*
 * One group, in a window like an item's (opened from the Groups list, or an item): its
 * name and pricing (Pricing tab: a change is a draft, saved for all its items at once, with the
 * prices it moves shown first; closing the window asks first), the name tags it sets for its items
 * (Name tags tab: saved like its pricing) and its items (Items tab: add or take them out, and update prices
 * and names that don't match the group; Needs attention says how many don't, linking there). The group
 * says how its items are taxed (part of its pricing), and its amounts are shown and typed that way
 * ("$100 inc GST"), stored before tax; switching its tax keeps them as typed ($100 ex GST). Delete is in its More menu (asked first): its items keep the pricing.
 */

const TABS = [
    { id: "details", label: "Details", icon: <FileText className="size-5" /> },
    { id: "pricing", label: "Pricing", icon: <Calculator className="size-5" /> },
    { id: "names", label: "Name tags", icon: <Tag className="size-5" /> },
    { id: "items", label: "Items", icon: <List className="size-5" /> },
    { id: "stats", label: "Stats", icon: <BarChart3 className="size-5" /> },
];

export function GroupWindow(
    props: Readonly<{
        data: PriceListData;
        id: string;
        tab: string;
        onTab: (tab: string) => void;
        onClose: () => void;
        onOpenItem: (uuid: string) => void;
        /** Just made with a made-up name: its name is ready to type over. */
        rename?: boolean;
    }>,
) {
    const saver = usePriceListSaver();
    const group = props.data.pricingGroups.find((g) => g.id === props.id);
    const [error, setError] = useState<Error | null>(null);
    // Duplicating it: its popup open (DuplicatePopup).
    const [duplicating, setDuplicating] = useState(false);
    const windows = usePriceListWindows();
    // A pricing change not saved yet: kept here, so it stays while another tab is open (marked on
    // the Pricing tab), and closing the window asks first, in the window.
    const [draft, setDraft] = useState<GroupDraft | null>(null);
    // Asking about unsaved changes before leaving them (closing, or opening another group here): a popup in the window (its `Here` in it).
    const windowConfirm = useWindowConfirm();
    // Bumped each time it asks, so the Pricing tab scrolls its draft into view.
    const [reveal, setReveal] = useState(0);
    // The Items tab showing only some of its items (from Needs attention): those needing attention, or not matching the group.
    const [only, setOnly] = useState<ItemsOnly>(null);
    // Its name tags being changed, not saved yet (its Name tags tab), kept like the pricing's draft.
    const [tagsDraft, setTagsDraft] = useState<GroupTags | null>(null);
    useEffect(() => {
        setDraft(null);
        setTagsDraft(null);
        setOnly(null);
    }, [props.id]);
    // It stops setting its pricing or name tags (here, or by someone else): a draft of them goes, with their tab.
    useEffect(() => {
        if (group?.setsPricing === false) setDraft(null);
    }, [group?.setsPricing]);
    useEffect(() => {
        if (group?.setsTags === false) setTagsDraft(null);
    }, [group?.setsTags]);
    const edited = group !== undefined && draft !== null && !sameDraft(draft, group, props.data.taxRates);
    const tagsEdited = group !== undefined && tagsDraft !== null && !sameTags(tagsDraft, group.tags);
    const unsaved = edited || tagsEdited;
    // Those to show in Needs attention: once moved off their tab (windows.ts).
    const left = useLeftUnsaved(props.tab, { pricing: { unsaved: edited, tab: "pricing" }, tags: { unsaved: tagsEdited, tab: "names" } });
    /** Ask before leaving what isn't saved (a popup: leave it, as `label` says, or keep editing), with it (and its save buttons) in view behind it: the pricing, else the name tags. */
    const ask = (leave: () => void, label: string) => {
        void windowConfirm.confirm({ title: "Leave without saving?", message: unsavedText(edited, tagsEdited), confirmLabel: label, cancelLabel: "Keep editing", tone: "danger" }).then((yes) => yes && leave());
        if (edited) {
            props.onTab("pricing");
            setReveal((n) => n + 1);
        } else props.onTab("names");
    };
    // Opening another group here while this one has changes not saved: it stays, flashes (windows.ts), and asks.
    useUnsavedGuard("group", unsaved, (proceed) => ask(proceed, "Discard and open the other group"));

    if (!group) {
        return (
            <RecordWindow kind="group" tone="violet" icon={<Layers className="size-4" />} title="Group not found" tabs={[]} tab="" onTab={() => undefined} onClose={props.onClose}>
                <Notice>This group doesn&apos;t exist any more.</Notice>
            </RecordWindow>
        );
    }
    // Its Pricing and Name tags tabs only when it sets them (its Details tab switches them).
    const tabs = withStatsTab(TABS, props.data).filter((t) => (t.id !== "pricing" || group?.setsPricing !== false) && (t.id !== "names" || group?.setsTags !== false));
    const tab = tabs.some((t) => t.id === props.tab) ? props.tab : "details";
    // Its items needing attention (the same checks as the Materials list): marked on the Items tab, said at the top.
    const attention = groupAttention(props.data, group.id);
    const run = (fn: () => Promise<unknown>) => {
        setError(null);
        fn().catch((err: Error) => setError(err));
    };
    return (
        <RecordWindow
            kind="group"
            share
            tone="violet"
            icon={<Layers className="size-4" />}
            title={
                <span className="inline-flex max-w-full items-center gap-2">
                    <span className="truncate">{group.name}</span>
                    {group.locksPrices ? <Lock className="size-3.5 shrink-0" aria-label="Prices locked" /> : null}
                </span>
            }
            label={group.name}
            subtitle={itemCount(membersOf(props.data, group.id).length, "item")}
            tabs={markedTabs(tabs, group, { pricing: edited, names: tagsEdited }, attention)}
            tab={tab}
            onTab={props.onTab}
            onClose={() => (unsaved ? ask(props.onClose, "Discard and close") : props.onClose())}
            actions={
                can(props.data, "groups")
                    ? [
                          groupLockAction(group.name, group.locksPrices, (locksPrices) => run(() => saver.updateGroup(group.id, { locksPrices }))),
                          duplicateAction("group", () => setDuplicating(true)),
                          deleteAction(group.name, () => run(() => saver.deleteGroup(group.id).then(props.onClose))),
                      ]
                    : []
            }
            notices={
                <>
                    <windowConfirm.Here />
                    <ErrorNotice error={error} />
                    <Issues
                        chips={
                            <>
                                {/* Changes not saved yet, once you've moved off their tab and until saved or cancelled, first (as an item's). */}
                                {left.has("pricing") ? <IssueChip tone="unsaved" label="Unsaved pricing" title="Its pricing has changes: save or cancel them." onShow={() => props.onTab("pricing")} /> : null}
                                {left.has("tags") ? <IssueChip tone="unsaved" label="Unsaved name tags" title="Its name tags have changes: save or cancel them." onShow={() => props.onTab("names")} /> : null}
                                <GroupIssueChips
                                    attention={attention}
                                    stale={outOfDate(props.data, group)}
                                    tab={tab}
                                    only={only}
                                    onShow={(which) => {
                                        setOnly(which);
                                        props.onTab("items");
                                    }}
                                />
                            </>
                        }
                    />
                    {whyNot(props.data, "groups") ? <Notice>{whyNot(props.data, "groups")}</Notice> : null}
                </>
            }
        >
            <div>
                {tab === "details" && <GroupDetails data={props.data} group={group} rename={props.rename} unsaved={{ setsPricing: edited, setsTags: tagsEdited }} />}
                {tab === "pricing" && <GroupPricing key={group.id} data={props.data} group={group} draft={draft} onDraft={setDraft} reveal={reveal} onItems={() => props.onTab("items")} />}
                {tab === "names" && <GroupNameTags key={group.id} data={props.data} group={group} draft={tagsDraft} onDraft={setTagsDraft} onItems={() => props.onTab("items")} />}
                {tab === "items" && (
                    <GroupItems key={group.id} data={props.data} group={group} onOpenItem={props.onOpenItem} attention={attention.problems} only={only} onOnly={setOnly} />
                )}
                {tab === "stats" && <GroupStatsTab id={group.id} onOpenItem={props.onOpenItem} />}
                <LeaveGuard window={["group", group.id]} when={unsaved} message={`The ${group.name} group: ${unsavedText(edited, tagsEdited)}`} />
                {duplicating ? (
                    <DuplicatePopup
                        data={props.data}
                        kind="group"
                        name={freeGroupName(props.data, group.name)}
                        withNumber={false}
                        supplierPrices={null}
                        // Its description, pricing, tax and name tags; no items (a new group starts empty), not locked.
                        onCreate={async (copy) => {
                            const made = await saver.createGroup(copy.name, group.rule);
                            await saver.updateGroup(made.id, { description: group.description, tax: group.tax, tags: group.tags, setsPricing: group.setsPricing, setsTags: group.setsTags });
                            windows.openGroup(made.id);
                        }}
                        onClose={() => setDuplicating(false)}
                    />
                ) : null}
            </div>
        </RecordWindow>
    );
}

/** Its tabs, marked: Pricing and Name tags while they've changes not saved (Pricing locked when it locks prices), Items when some need attention. */
function markedTabs<T extends { id: string }>(tabs: readonly T[], group: PricingGroupRow, unsaved: { pricing: boolean; names: boolean }, attention: { count: number; danger: boolean }) {
    return tabs.map((t) => {
        if (t.id === "pricing") return { ...t, unsaved: unsaved.pricing, locked: group.locksPrices ? "Prices locked" : undefined };
        if (t.id === "names") return { ...t, unsaved: unsaved.names };
        if (t.id === "items" && attention.count) return { ...t, attention: { danger: attention.danger, label: attentionText(attention.count) } };
        return t;
    });
}

/** Its More menu's Delete group (asked first): its items keep their pricing. */
const deleteAction = (name: string, run: () => void) => ({
    icon: <Trash2 className="size-4" />,
    label: "Delete group",
    help: "Its items keep their pricing. No prices change.",
    danger: true,
    confirm: { title: `Delete the ${name} group?`, message: "Its items keep their pricing. No prices change.", confirmLabel: "Delete group" },
    run,
});

/**
 * Its Needs attention pills: items needing attention, and prices, costs and names to update (each the Items tab showing just them,
 * `onShow`; not what the tab open lists itself: its Pricing tab its prices, its Name tags tab its names), and items it can't
 * price (no cost: nothing to show here, said on hover; each is set on its item's Costing tab). None while the Items tab shows them.
 */
function GroupIssueChips(
    props: Readonly<{ attention: { count: number; danger: boolean }; stale: { prices: number; costs: number; names: number; noCost: number }; tab: string; only: ItemsOnly; onShow: (only: "attention" | "updates") => void }>,
) {
    const { attention, stale, tab } = props;
    const showing = (which: ItemsOnly) => tab === "items" && props.only === which;
    const prices = tab === "pricing" ? 0 : stale.prices;
    const names = tab === "names" ? 0 : stale.names;
    return (
        <>
            {attention.count && !showing("attention") ? (
                <IssueChip tone={attention.danger ? "error" : "warning"} label={attentionText(attention.count)} onShow={() => props.onShow("attention")} />
            ) : null}
            {(prices || stale.costs || names) && !showing("updates") ? (
                <IssueChip label={toUpdate(prices, stale.costs, names)} title={outOfStep(prices, stale.costs, names)} onShow={() => props.onShow("updates")} />
            ) : null}
            {stale.noCost ? <IssueChip label={`${itemCount(stale.noCost, "item")} with no cost`} title={noCostMessage(stale.noCost)} /> : null}
        </>
    );
}

/**
 * A group's details: its name (selected to type over when it's just been made: `rename`) and what
 * it's for, a few words for whoever picks a group for an item. Only kept here.
 */
function GroupDetails({ data, group, rename, unsaved }: Readonly<{ data: PriceListData; group: PricingGroupRow; rename?: boolean; unsaved: Unsaved }>) {
    const saver = usePriceListSaver();
    const canEdit = can(data, "groups");
    return (
        <Box>
            <TextField
                label="Name"
                selectOnOpen={rename}
                value={group.name}
                disabled={!canEdit}
                validate={(v) => (v ? null : "Give the group a name")}
                onSave={(name) => saver.updateGroup(group.id, { name })}
            />
            <TextField
                label="Description"
                multiline
                value={group.description}
                placeholder="What it's for, e.g. which items belong in it"
                disabled={!canEdit}
                hint="Only kept here: never sent to ServiceM8."
                onSave={(description) => saver.updateGroup(group.id, { description })}
            />
            <GroupSets data={data} group={group} unsaved={unsaved} />
        </Box>
    );
}

/** Changes to its pricing, and to its name tags, not saved yet: turning that off drops them (said when it asks). */
type Unsaved = Record<"setsPricing" | "setsTags", boolean>;

/**
 * What a group sets for its items, a switch each: its pricing, its name tags. Either off (or both: just a group of items, for its
 * stats or its lock), its items are priced (named) their own way. Asked first: off, they keep its pricing as their own (no price
 * changes); on, they follow it again (those it gives another price, or name, show as to update; keeping one overrides it).
 */
function GroupSets({ data, group, unsaved }: Readonly<{ data: PriceListData; group: PricingGroupRow; unsaved: Unsaved }>) {
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const canEdit = can(data, "groups");
    const count = itemCount(membersOf(data, group.id).length, "item");
    const flip = async (field: "setsPricing" | "setsTags", on: boolean) => {
        const what = field === "setsPricing" ? "pricing" : "name tags";
        const yes = await confirm({
            title: on ? `Set its items' ${what} again?` : `Stop setting its items' ${what}?`,
            subject: group.name,
            message: on
                ? `Its ${count} follow the group's ${what} again. Any it gives another ${field === "setsPricing" ? "price" : "name"} show as to update (keep one to override it).`
                : `Its ${count} keep ${field === "setsPricing" ? "the group's pricing as their own: no prices change" : "their names as they are"}, and are ${field === "setsPricing" ? "priced" : "named"} their own way from now.${unsaved[field] ? ` Your changes to its ${what}, not saved yet, are dropped.` : ""}`,
            confirmLabel: on ? `Set ${what}` : `Stop setting ${what}`,
            tone: "primary",
        });
        if (yes) await saver.updateGroup(group.id, { [field]: on });
    };
    return (
        // A small title over each, as the fields above have theirs.
        <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
                <span className="field-label block">Pricing</span>
                <ToggleField
                    label="Sets its items' pricing"
                    checked={group.setsPricing}
                    disabled={!canEdit}
                    hint={group.setsPricing ? "Its Pricing tab prices them all." : "Each is priced its own way."}
                    ask={(on) => void flip("setsPricing", on)}
                />
            </div>
            <div className="space-y-1.5">
                <span className="field-label block">Name tags</span>
                <ToggleField
                    label="Sets its items' name tags"
                    checked={group.setsTags}
                    disabled={!canEdit}
                    hint={group.setsTags ? "Its Name tags tab sets them for all." : "Each is named its own way."}
                    ask={(on) => void flip("setsTags", on)}
                />
            </div>
        </div>
    );
}

/**
 * Its pricing: a draft until it's saved for every item in the group (GroupSave).
 * Each time `reveal` goes up (closing the window with the draft unsaved), the draft and its save
 * buttons are scrolled into view.
 */
function GroupPricing(
    props: Readonly<{ data: PriceListData; group: PricingGroupRow; draft: GroupDraft | null; onDraft: (draft: GroupDraft | null) => void; reveal: number; /** Its Items tab. */ onItems: () => void }>,
) {
    const { data, group, draft, onDraft: setDraft } = props;
    // Saving and updating (or showing what couldn't be): its fields wait, so nothing's changed that the run would then drop.
    const [running, setRunning] = useState(false);
    const canEdit = can(data, "groups") && !running;
    const pricing = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (props.reveal) pricing.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [props.reveal]);
    // How long its items usually take on jobs (its Stats tab's), shown by Labour: with See stats.
    const jobTime = jobTimeShown(useGroupJobTime(group.id, can(data, "stats")));
    const { rule, tax } = draft ?? { rule: group.rule, tax: group.tax };
    // A change back to what's saved is no change: the draft goes.
    const toDraft = (next: GroupDraft) => {
        setDraft(sameDraft(next, group, data.taxRates) ? null : next);
        return Promise.resolve();
    };
    const change = (patch: Partial<PricingRule>) => toDraft({ rule: { ...rule, ...patch }, tax });
    // A set price is the same for every item in it (no cost involved), so it's shown as an item's is; worked out from each item's cost, it isn't.
    const setPrice = rule.mode === "manual" ? calcPrice({ cost: "0", price_includes_taxes: tax.includesTax }, groupTaxBasis(data, tax).tax, rule, data.settings)?.price : undefined;
    // Its typed amounts stay as they read ($100 inc GST becomes $100 ex GST): pricing.ts keepShownAmounts.
    const changeTax = (patch: Partial<GroupTax>) => {
        const next = { ...tax, ...patch };
        const basisOf = (t: GroupTax) => {
            const b = groupTaxBasis(data, t);
            return { includesTax: b.includesTax, tax: b.tax };
        };
        return toDraft({ rule: keepShownAmounts(rule, basisOf(tax), basisOf(next)), tax: next });
    };

    return (
        <>
            {/* The same card and editor as an item's own pricing (PricingTab.tsx, PricingEditor.tsx): with a set price, the price every
                item gets; else a Tax dropdown where an item shows its price. */}
            <Box>
                <PricingCard aside={`Used by its ${itemCount(membersOf(data, group.id).length, "item")}.`}>
                    <PricingEditor
                        data={data}
                        rule={rule}
                        tax={tax}
                        canEdit={canEdit}
                        price={setPrice}
                        taxNote="Every item in the group is taxed this way."
                        editing={draft !== null}
                        jobTime={jobTime}
                        // Its save bar; not being edited, in the same place, its prices and costs that need updating now (if any).
                        footer={draft ? <GroupSave data={data} group={group} draft={draft} onRunning={setRunning} onDone={() => setDraft(null)} /> : <GroupPricesToUpdate data={data} group={group} onItems={props.onItems} />}
                        boxRef={pricing}
                        onRule={change}
                        onMode={(mode) => change({ mode })}
                        onTax={changeTax}
                    />
                </PricingCard>
            </Box>
        </>
    );
}

/** The Items tab showing only those needing attention, or those not matching the group (prices or names); null: all. */
type ItemsOnly = "attention" | "updates" | null;

/**
 * "9 items", "3 of 9 items" (narrowed: searched, or only some shown), or none yet (`text`); and, on hover (`title`), with how
 * many override its price, its tags, and are archived ("9 items, 2 price overrides, 1 tag override, 1 archived").
 */
function countText(shown: number, all: number, narrowed: boolean, archived: number, overriding: number, tagOverriding: number) {
    if (!all) return { text: "No items in it yet.", title: undefined };
    const count = narrowed ? `${shown.toLocaleString()} of ${itemCount(all, "item")}` : itemCount(all, "item");
    const extras = [overriding ? itemCount(overriding, "price override") : "", tagOverriding ? itemCount(tagOverriding, "tag override") : "", archived ? `${archived.toLocaleString()} archived` : ""].filter(Boolean);
    // Just how many, so the header stays one line: the rest on hover (each row has its own badges).
    return { text: count, title: extras.length ? [count, ...extras].join(", ") : undefined };
}

/** Pricing as a rule of its own (its rule's fields only), e.g. to put back what an item had. */
const ruleOf = (p: ItemPricing): PricingRule => ({
    mode: p.mode,
    markupValue: p.markupValue,
    basePrice: p.basePrice,
    labourMinutes: p.labourMinutes,
    labourUnit: p.labourUnit,
    roundMode: p.roundMode,
    roundTo: p.roundTo,
});

/** How a group's items can be listed (its Items tab). */
const ITEM_SORTS = [
    { value: "name", label: "Name" },
    { value: "number", label: "Item number" },
    { value: "price-high", label: "Price, high to low" },
    { value: "price-low", label: "Price, low to high" },
    { value: "update", label: "To update first" },
] as const;
type ItemSort = (typeof ITEM_SORTS)[number]["value"];

/** A group's items as its Items tab lists them: each with its price to update (if the group prices it), its name to update, and what it overrides; sorted, archived last. */
function groupItemRows(data: PriceListData, group: PricingGroupRow, sort: ItemSort, fmt: ReturnType<typeof useMoney>) {
    return (
        membersOf(data, group.id)
            .map((m) => {
                const itemRate = itemTax(data, m);
                const pricing = data.itemPricing[m.uuid];
                // Overriding the group's price: its price is its own business (its window), not the group's to update. Its name still is.
                const byGroup = pricedByGroup(pricing);
                return {
                    m,
                    rate: itemRate.rate,
                    check: byGroup ? needsCheck(partItem(data, m), itemRate, pricing, data.settings) : null,
                    nameTo: group.setsTags ? groupTagsName(m.name, group.tags, data.nameTags, pricing?.tagOverrides) : null,
                    noCost: byGroup && missingCost(partItem(data, m), pricing),
                    // Its cost to update (any item's, however it's priced: supplierPrices.ts costUpdates), in its basis; none without See costs.
                    costTo: data.costUpdates[m.uuid] ?? null,
                    // Its own pricing overriding the group's (not when the group doesn't set pricing: there's nothing to override).
                    overrides: byGroup || !group.setsPricing ? null : describePricing(pricing, { includesTax: m.price_includes_taxes, tax: itemRate.tax }, fmt),
                    // The group's tags it sets itself, by name (those still in settings; none when it doesn't set tags).
                    ownTags: group.setsTags ? ownKeys(pricing?.tagOverrides).map((key) => tagName(key, data.nameTags)).filter(Boolean) : [],
                };
            })
            .sort(itemSorter(sort, (m) => itemName(data, m)))
            // Archived ones last, however it's sorted (still in it: restored, they're priced by it again).
            .sort((a, b) => Number(!a.m.active) - Number(!b.m.active))
    );
}
type GroupItemRow = ReturnType<typeof groupItemRows>[number];

/** Whether a row is listed, narrowed to some (`only`): those needing attention, or those with a price, cost or name to update. */
function inView(row: GroupItemRow, only: ItemsOnly, attention: ReadonlyMap<string, ItemProblem[]>) {
    if (only === "attention") return attention.has(row.m.uuid);
    if (only === "updates") return toUpdateRow(row);
    return true;
}

/** A price, cost or name to update on it. */
const toUpdateRow = (r: Pick<GroupItemRow, "check" | "costTo" | "nameTo">) => r.check !== null || r.costTo !== null || r.nameTo !== null;

/**
 * Its items: searched and added at the top, then each with its price and name, and when the group gives another price or its
 * name tags read differently, the new ones: ticked, then updated together (locked prices asked about), as the person clicking.
 * Taking one out keeps its price (asked first). The list can be sorted, and narrowed (from Needs attention).
 */
function GroupItems({
    data,
    group,
    onOpenItem,
    attention,
    only,
    onOnly,
}: Readonly<{
    data: PriceListData;
    group: PricingGroupRow;
    onOpenItem: (uuid: string) => void;
    /** Its items needing attention, with what (attention.ts groupAttention). */
    attention: ReadonlyMap<string, ItemProblem[]>;
    /** Only some listed: those needing attention, or not matching the group (chosen from Needs attention; cleared here). */
    only: ItemsOnly;
    onOnly: (only: ItemsOnly) => void;
}>) {
    const [sort, setSort] = useState<ItemSort>("name");
    // Searching its items (name, item number, full name).
    const [filter, setFilter] = useState("");
    // The items ticked, to update or take out together (the list's header).
    const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
    const fmt = useMoney();
    const rows = groupItemRows(data, group, sort, fmt);
    const shown = rows.filter((r) => inView(r, only, attention) && matchesSearch(`${itemName(data, r.m)} ${r.m.item_number} ${r.m.name}`, filter));
    // Ticked, of those shown (a search narrows it).
    const picked = shown.filter((r) => selected.has(r.m.uuid));
    const clear = () => setSelected(new Set());
    const actions = usePickedActions(data, group, picked, clear);
    const tick = (uuid: string, on: boolean) =>
        setSelected((now) => {
            const next = new Set(now);
            if (on) next.add(uuid);
            else next.delete(uuid);
            return next;
        });
    const canEdit = can(data, "groups");
    return (
        <>
            {/* Searching its items, and adding some (a popup in the window: a search with an Add on each). */}
            <Box>
                <div className="flex items-center gap-2">
                    <label className="relative block min-w-0 flex-1">
                        <span className="sr-only">Search this group's items</span>
                        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
                        <input className="input pl-9" placeholder="Search this group's items" value={filter} onChange={(e) => setFilter(e.target.value)} />
                    </label>
                    {canEdit ? <AddItems data={data} group={group} /> : null}
                </div>
            </Box>
            <Box className="py-0">
                {/* The list's own header, pinned while it scrolls and always the same height: the tick box for them all, then either how many
                    and how they're listed, or (some ticked) what's ticked and what to do with it, in the same place, so nothing moves. */}
                <div className="sticky top-0 z-10 -mx-4 flex h-11 items-center gap-3 border-b border-line bg-surface px-4 text-xs text-muted">
                    {canEdit && shown.length ? <TickAll shown={shown.length} picked={picked.length} disabled={actions.progress !== null} onAll={(all) => setSelected(all ? new Set(shown.map((r) => r.m.uuid)) : new Set())} /> : null}
                    {picked.length && canEdit ? (
                        <PickedBar picked={picked} actions={actions} />
                    ) : (
                        <ListBar data={data} group={group} rows={rows} shown={shown.length} narrowed={only !== null || filter.trim() !== ""} only={only} onOnly={onOnly} sort={sort} onSort={setSort} />
                    )}
                </div>
                {rows.length > 0 && shown.length === 0 ? <p className="m-0 py-3 text-sm text-muted">No items in it match.</p> : null}
                {shown.length ? (
                    <ul className="-mx-4 divide-y divide-line">
                        {shown.map((row) => (
                            <ItemRow
                                key={row.m.uuid}
                                data={data}
                                group={group}
                                row={row}
                                canEdit={canEdit}
                                picked={selected.has(row.m.uuid)}
                                onPick={(on) => tick(row.m.uuid, on)}
                                onOpen={() => onOpenItem(row.m.uuid)}
                                problems={attention.get(row.m.uuid) ?? []}
                                saving={actions.saving(row.m.uuid)}
                                failure={actions.failure(row.m.uuid)}
                                // One run at a time: its ticks wait while one runs.
                                running={actions.progress !== null}
                            />
                        ))}
                    </ul>
                ) : null}
                <ErrorNotice error={actions.error} />
            </Box>
        </>
    );
}

/**
 * What can be done with the ticked items, one at a time (each a ServiceM8 write, as the person clicking), with how far it's got
 * (`progress`): their prices updated to the group's (locked ones among them asked about once, held), their costs to what their
 * labour, supplier prices or parts give, their names to its name
 * tags, or taken out of it (asked once: each keeps the price it has, and its pricing as its own). Done: they're unticked.
 */
function usePickedActions(data: PriceListData, group: PricingGroupRow, picked: readonly GroupItemRow[], clear: () => void) {
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const ask = useGroupUpdateConfirm(data);
    const [error, setError] = useState<Error | null>(null);
    const [progress, setProgress] = useState<string | null>(null);
    // Where each is (its row shows a spinner while it's saved).
    const updates = useRowUpdates();
    const run = (uuids: string[], update: (uuid: string) => Promise<unknown>, what: string) => {
        setError(null);
        updates.clear();
        updateEach(uuids, update, what, setProgress, updates.track(what))
            .then(clear)
            .catch((err: Error) => setError(err))
            .finally(updates.settle);
    };
    const saving = (uuid: string) => [...updates.rows].some(([key, row]) => key.endsWith(`:${uuid}`) && row.state === "updating");
    // Turned down in the last run (kept until the next): why, for its row.
    const failure = (uuid: string) => {
        for (const [key, row] of updates.rows) if (key.endsWith(`:${uuid}`) && row.state === "failed") return row.error;
        return null;
    };
    const prices = picked.filter((r) => r.check);
    const names = can(data, "items.edit") ? picked.filter((r) => r.nameTo !== null) : [];
    // Costs: only for those who may change them.
    const costs = can(data, "costs.edit") ? picked.filter((r) => r.costTo !== null) : [];
    const updateCosts = async () => {
        if (await ask.costs(costs.length)) run(costs.map((r) => r.m.uuid), saver.updateCost, "costs");
    };
    // Each asked first (as its Pricing tab's are): they change many items at once.
    const updatePrices = async () => {
        const uuids = prices.map((r) => r.m.uuid);
        if (await ask.prices(uuids)) run(uuids, (uuid) => saver.updateToPricing(uuid, priceLock(data, uuid) !== null), "prices");
    };
    const updateNames = async () => {
        if (await ask.names(names.length)) run(names.map((r) => r.m.uuid), saver.updateNameToGroup, "names");
    };
    const takeOut = async () => {
        const n = picked.length;
        const one = picked[0];
        const stayLocked = n === 1 ? " Its price stays locked." : " Their prices stay locked.";
        const yes = await confirm({
            title: n === 1 ? `Take it out of the ${group.name} group?` : `Take ${n} items out of the ${group.name} group?`,
            subject: n === 1 && one ? itemName(data, one.m) : undefined,
            message: `${n === 1 ? "It keeps its price" : "Each keeps its price"}, and its pricing as its own.${group.locksPrices ? stayLocked : ""}`,
            confirmLabel: "Take out",
            tone: "danger",
        });
        if (!yes) return;
        // Out of the group (its rule as it is now becomes its own), keeping the price it has if the group gave another.
        const keep = new Map(picked.map((r) => [r.m.uuid, r.check !== null]));
        run([...keep.keys()], (uuid) => saver.takeOutOfGroup(uuid, keep.get(uuid) ?? false), "items");
    };
    return { prices: prices.length, costs: costs.length, names: names.length, progress, error, saving, failure, updatePrices, updateCosts, updateNames, takeOut };
}

/** Adding items to the group: Add item, then a popup in the window, a search with an Add on each (any active item not in it). */
function AddItems({ data, group }: Readonly<{ data: PriceListData; group: PricingGroupRow }>) {
    const saver = usePriceListSaver();
    const [adding, setAdding] = useState(false);
    // Each added one's pricing before, so Undo (a mis-click) puts it back exactly: its old group, its own rule, or a typed price.
    const before = useRef(new Map<string, ItemPricing | undefined>());
    const add = (uuid: string) => {
        before.current.set(uuid, data.itemPricing[uuid]);
        return saver.joinGroup(uuid, group.id);
    };
    // Back as it was, in one step: in its old group or none, with its own rule if it had one (overriding there, or priced its own way).
    const undoAdd = (uuid: string) => {
        const was = before.current.get(uuid);
        return saver.rejoinGroup(uuid, was?.groupId ?? null, was && !pricedByGroup(was) ? ruleOf(was) : null);
    };
    return (
        <>
            <button type="button" className="btn-secondary shrink-0" onClick={() => setAdding(true)}>
                <Plus className="size-4" /> Add item
            </button>
            {adding ? (
                <WindowPopup top title={`Add items to the ${group.name} group`} onClose={() => setAdding(false)}>
                    <AddPicker inline placeholder="Search by name or item number" options={addChoices(data, group)} onAdd={add} onUndo={undoAdd} />
                </WindowPopup>
            ) : null}
        </>
    );
}

/** What can be added: any active item not in the group; one in another group says which, briefly (in full on hover), and takes a second click. */
function addChoices(data: PriceListData, group: PricingGroupRow) {
    return data.materials
        .filter((m) => m.active && data.itemPricing[m.uuid]?.groupId !== group.id)
        .map((m) => {
            const from = data.pricingGroups.find((g) => g.id === data.itemPricing[m.uuid]?.groupId);
            return {
                value: m.uuid,
                label: itemName(data, m),
                synced: m.synced,
                detail: m.item_number || undefined,
                note: from ? `In ${from.name}` : undefined,
                noteTitle: from ? `In the ${from.name} group: adding it moves it to this one` : undefined,
                confirm: from !== undefined,
            };
        });
}

/** The list's tick box: none ticked, ticks every one shown; any ticked (some: shown part-ticked), unticks them all (as Gmail's). */
function TickAll({ shown, picked, disabled, onAll }: Readonly<{ shown: number; picked: number; disabled?: boolean; onAll: (all: boolean) => void }>) {
    const all = picked === shown;
    const label = picked ? "Untick them all" : "Tick every item shown";
    return (
        <input
            type="checkbox"
            className="size-4 shrink-0 accent-brand-600"
            aria-label={label}
            title={label}
            checked={all}
            disabled={disabled}
            ref={(el) => {
                if (el) el.indeterminate = picked > 0 && !all;
            }}
            onChange={() => onAll(picked === 0)}
        />
    );
}

/** The list's header with some ticked: how many, how far an update has got, and what can be done with them (each kind of update only when it applies). */
function PickedBar({ picked, actions }: Readonly<{ picked: readonly GroupItemRow[]; actions: ReturnType<typeof usePickedActions> }>) {
    const update = "btn-primary px-2.5 py-1 text-xs";
    return (
        <>
            {/* On one line, as the rest: unticked with the tick box beside it. */}
            <span className="shrink-0 font-medium whitespace-nowrap text-ink">{picked.length.toLocaleString()} selected</span>
            {actions.progress !== null ? (
                // Updating: just how far it's got (its row has a spinner), in the buttons' place: they can't be pressed meanwhile.
                <span className="ml-auto flex min-w-0 items-center gap-2 whitespace-nowrap">
                    <Spinner className="size-4 shrink-0" /> <span className="truncate">{actions.progress}</span>
                </span>
            ) : (
                <span className="ml-auto flex shrink-0 items-center gap-2 whitespace-nowrap">
                    {actions.prices ? (
                        <button type="button" className={update} title={`${itemCount(actions.prices, "price")} to what the group gives`} onClick={() => void actions.updatePrices()}>
                            Update {actions.prices === 1 ? "price" : `${actions.prices} prices`}
                        </button>
                    ) : null}
                    {actions.costs ? (
                        <button
                            type="button"
                            className={update}
                            title={`${itemCount(actions.costs, "cost")} to what ${actions.costs === 1 ? "its" : "their"} labour, supplier price or parts give`}
                            onClick={() => void actions.updateCosts()}
                        >
                            Update {actions.costs === 1 ? "cost" : `${actions.costs} costs`}
                        </button>
                    ) : null}
                    {actions.names ? (
                        <button type="button" className={update} title={`${itemCount(actions.names, "name")} to the group's name tags`} onClick={() => void actions.updateNames()}>
                            Update {actions.names === 1 ? "name" : `${actions.names} names`}
                        </button>
                    ) : null}
                    <button type="button" className="btn-secondary px-2.5 py-1 text-xs" onClick={() => void actions.takeOut()}>
                        Take out of group
                    </button>
                </span>
            )}
        </>
    );
}

/**
 * The list's header with none ticked, on one line however narrow (each part kept whole, the count cut short first): how many,
 * what it's narrowed to (cleared with its ×), its items in the Materials list (keeping the windows open), and how it's sorted.
 */
function ListBar(
    props: Readonly<{
        data: PriceListData;
        group: PricingGroupRow;
        rows: readonly GroupItemRow[];
        shown: number;
        narrowed: boolean;
        only: ItemsOnly;
        onOnly: (only: ItemsOnly) => void;
        sort: ItemSort;
        onSort: (sort: ItemSort) => void;
    }>,
) {
    const { rows, only } = props;
    // Just the group's items: the open windows go along with it (RecordWindow.tsx), this one too.
    const listQuery = new URLSearchParams({ inGroup: props.group.id });
    const counted = countText(props.shown, rows.length, props.narrowed, rows.filter((r) => !r.m.active).length, rows.filter((r) => r.overrides !== null).length, rows.filter((r) => r.ownTags.length > 0).length);
    const attentionOnly = only === "attention";
    return (
        <>
            <span className="min-w-0 truncate" title={counted.title}>
                {counted.text}
            </span>
            {only ? (
                <button
                    type="button"
                    className="inline-flex shrink-0 items-center gap-1 rounded-full bg-warning-tint px-2 py-0.5 whitespace-nowrap text-warning-ink"
                    title={attentionOnly ? "Showing those needing attention: show all its items" : "Showing those with prices, costs or names to update: show all its items"}
                    onClick={() => props.onOnly(null)}
                >
                    {attentionOnly ? "Needing attention" : "To update"} <X className="size-3" />
                </button>
            ) : null}
            {rows.length ? (
                <Link href={`/t/${meta.id}/materials?${listQuery.toString()}`} className="ml-auto inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap" title="See them in the Materials list: their costs, markups and profits side by side">
                    Materials list <ArrowUpRight className="size-3.5" />
                </Link>
            ) : null}
            {rows.length > 1 ? (
                <span className="shrink-0 whitespace-nowrap">
                    <InlinePicker label="Sort by" options={ITEM_SORTS} value={props.sort} onChange={props.onSort} />
                </span>
            ) : null}
        </>
    );
}

/** Items with no cost: the group works from the cost, so it can't price them (they keep their prices). */
const noCostMessage = (n: number) =>
    `${itemCount(n, "item")} ${n === 1 ? "has" : "have"} no cost, so the group can't work out ${n === 1 ? "its price" : "their prices"}: ${n === 1 ? "it keeps its" : "they keep theirs"} until ${n === 1 ? "it has" : "they have"} one (set on each item's Costing tab).`;

/** "7 prices", "2 costs", "1 name": those there are, joined ("7 prices, 2 costs and 1 name"). */
function updateThings(prices: number, costs: number, names: number) {
    const things = [prices ? itemCount(prices, "price") : "", costs ? itemCount(costs, "cost") : "", names ? itemCount(names, "name") : ""].filter(Boolean);
    return things.length > 1 ? `${things.slice(0, -1).join(", ")} and ${things.at(-1)}` : (things[0] ?? "");
}

/** "7 prices and 2 names to update", "1 cost to update". */
const toUpdate = (prices: number, costs: number, names: number) => `${updateThings(prices, costs, names)} to update`;

/** On hover: "7 prices and 2 names don't match the group yet. 1 cost isn't what its labour, supplier price or parts give now." */
function outOfStep(prices: number, costs: number, names: number) {
    const lines: string[] = [];
    if (prices || names) lines.push(`${updateThings(prices, 0, names)} ${prices + names === 1 ? "doesn't" : "don't"} match the group yet.`);
    if (costs) lines.push(`${itemCount(costs, "cost")} ${costs === 1 ? "isn't" : "aren't"} what ${costs === 1 ? "its" : "their"} labour, supplier price or parts give now.`);
    return lines.join(" ");
}

/** What updating a row's price does (its hover text): "Update its price to $50.00 inc GST (taxed as the group says)". */
function updateTitle(check: NeedsCheck, basisTo: string | null, money: (amount: string) => string) {
    const taxed = basisTo ? " " + basisTo + " (taxed as the group says)" : "";
    return `Update its price to ${money(check.target)}${taxed}`;
}

/**
 * One item in the group, on two lines. First its name (opens it), item number and what needs
 * attention or archived; on the right its price (RowPrice). Under them its full name as it reads in
 * ServiceM8 (RowName). A tick box first, to update or take it out with others from the list's header.
 */
function ItemRow(
    props: Readonly<{
        data: PriceListData;
        group: PricingGroupRow;
        row: GroupItemRow;
        /** What needs attention on it (attention.ts), as in the Materials list: a mark, its row tinted. */
        problems: readonly ItemProblem[];
        canEdit: boolean;
        /** Ticked (to update or take out with others, from the list's header). */
        picked: boolean;
        onPick: (on: boolean) => void;
        onOpen: () => void;
        /** Being updated (with others ticked): a spinner by its name. */
        saving?: boolean;
        /** Turned down in the last update (why): said by its name. */
        failure?: string | null;
        /** An update's running: its tick box waits. */
        running?: boolean;
    }>,
) {
    const { data, row } = props;
    const { m } = row;
    const name = itemName(data, m);
    const danger = props.problems.some((p) => p.danger);
    const fmt = useMoney();
    // Its mark says what needs attention, and its cost to update (no badge for it: the group's own update, its row tinted).
    const marks = [...props.problems.map((p) => p.text), ...(row.costTo === null ? [] : [`Cost to update: ${fmt.money(m.cost)} → ${fmt.money(row.costTo)}`])];
    return (
        <li className={clsx("grid items-center gap-x-3 gap-y-1 px-4 py-2.5", props.canEdit ? "grid-cols-[auto_minmax(0,1fr)_auto]" : "grid-cols-[minmax(0,1fr)_auto]", rowTint(props.picked, props.problems.some(tintsRow) || toUpdateRow(row), danger))}>
            {props.canEdit ? (
                <input type="checkbox" className="size-4 accent-brand-600" aria-label={`Tick ${name}`} checked={props.picked} disabled={props.running} onChange={(e) => props.onPick(e.target.checked)} />
            ) : null}
            <span className="flex min-w-0 items-center gap-2">
                {props.saving ? <Spinner className="size-3.5 shrink-0" /> : null}
                <SyncBadge synced={m.synced} />
                <button type="button" className={clsx("link-button truncate text-left font-medium", !m.active && "opacity-60")} onClick={props.onOpen}>
                    {name}
                </button>
                {/* Its item number after the name, small (amber when it has none: every item needs one). */}
                <span className={clsx("shrink-0 text-xs", m.item_number ? "text-muted" : "text-warning-ink")}>{m.item_number || "No item number"}</span>
                <NeedsAttention problems={marks} danger={danger} />
                {props.failure ? (
                    <span className="shrink-0 text-xs text-danger" title={props.failure}>
                        Couldn&apos;t update
                    </span>
                ) : null}
                {m.active ? null : <Badge title="Archived in ServiceM8, so it isn't offered on new jobs. It's still in the group: restored, it's priced by it again.">Archived</Badge>}
            </span>
            <RowPrice data={data} row={row} />
            {props.canEdit ? <span /> : null}
            <RowName row={row} tags={data.nameTags} />
        </li>
    );
}

/** A row's tint: ticked, else red with something wrong, else amber with something to see to (a price, cost or name to update too). */
function rowTint(picked: boolean, attention: boolean, danger: boolean) {
    if (picked) return "bg-brand-500/5";
    if (danger) return "bg-danger-soft";
    return attention ? "bg-warning-soft" : undefined;
}

/**
 * A row's price (the group's new one beside it when it gives another, with its tax when the group taxes it otherwise), and before
 * it what's to know about it: what it overrides of the group's (its only badge: no cost and a cost to update are its row's
 * attention mark and tint, said on hover there), then right by the price its lock (its own, or the group's: on every row then).
 */
function RowPrice({ data, row }: Readonly<{ data: PriceListData; row: GroupItemRow }>) {
    const fmt = useMoney();
    const { m, check, overrides, ownTags } = row;
    const basis = basisLabel(m.price_includes_taxes, row.rate);
    const basisTo = check?.taxTo ? basisLabel(check.taxTo.includesTax, groupTaxBasis(data, check.taxTo).rate) : null;
    const tagsToo = ownTags.length ? ` And its name tags for ${ownTags.join(", ")}.` : "";
    const why = check?.reason === "edited" ? "Its price was changed in ServiceM8" : "Its group's pricing gives a new price";
    return (
        <span className="flex items-center justify-end gap-2 text-sm whitespace-nowrap tabular-nums">
            {/* What it overrides, by its price: its price, its tags, or both (one badge). */}
            <OverrideBadge overrides={overrides} ownTags={ownTags} tagsToo={tagsToo} />
            {/* Locked (by itself, or by the group: every row then): right before the price it's about, wherever a badge is or isn't. */}
            <LockMark lock={priceLock(data, m.uuid)} />
            <span>
                {check ? (
                    <span title={`${why}: ${updateTitle(check, basisTo, fmt.money)}`}>
                        <span className="text-muted">
                            {fmt.money(m.price)}
                            {basisTo ? <span className="text-xs"> {basis}</span> : null}
                        </span>{" "}
                        → <span className="font-semibold text-warning-ink">{fmt.money(check.target)}</span>
                    </span>
                ) : (
                    fmt.money(m.price)
                )}{" "}
                <span className="text-xs text-muted">{basisTo ?? basis}</span>
            </span>
        </span>
    );
}

/** What a row overrides of its group's: its price ("Price override"), its name tags ("Tag override"), or both; nothing: none. */
function OverrideBadge({ overrides, ownTags, tagsToo }: Readonly<{ overrides: string | null; ownTags: readonly string[]; tagsToo: string }>) {
    if (overrides === null && !ownTags.length) return null;
    if (overrides === null) {
        return (
            <Badge tone="blue" title={`Overrides the group's name tags for ${ownTags.join(", ")}: its own. The rest follow the group. Change it in its window.`}>
                Tag override
            </Badge>
        );
    }
    return (
        <Badge tone="blue" title={`Overrides the group's price, priced its own way: ${overrides}.${tagsToo} Change it in its window.`}>
            {ownTags.length ? "Price & tag override" : "Price override"}
        </Badge>
    );
}

/**
 * A row's full name as it reads in ServiceM8, all of it: each of its lines, wrapping as it needs. As it is: its tags coloured, as in
 * the Materials list. About to change (the tags it should have read differently in it): the change marked instead, line by line.
 */
function RowName({ row, tags }: Readonly<{ row: GroupItemRow; tags: NameTagConfig }>) {
    if (row.nameTo === null) {
        return (
            <div className="col-span-2 min-w-0 pl-6 text-xs whitespace-pre-line text-muted">
                <ServiceM8Name name={row.m.name} tags={tags} />
            </div>
        );
    }
    return (
        <div className="col-span-2 min-w-0 pl-6 text-xs text-muted" title={`Its name with the group's name tags: ${row.nameTo}`}>
            <NameChange from={row.m.name} to={row.nameTo} />
        </div>
    );
}

/** A name's change, line by line (web/lib/diff.ts lineDiff): removed words struck through, added ones highlighted. */
function NameChange({ from, to }: Readonly<{ from: string; to: string }>) {
    return (
        <>
            {lineDiff(from, to).map((line, i) => (
                // Its lines, in order (the same text can be on two: its place tells them apart).
                <div key={`${i}:${line.map((part) => part.text).join("")}`}>
                    <DiffLine parts={line} />
                </div>
            ))}
        </>
    );
}

/** Sorting a group's items: by name, item number, price either way, or those to update (price, cost or name) first; ties by name. */
function itemSorter(sort: ItemSort, nameOf: (m: MaterialRow) => string) {
    type Row = { m: MaterialRow; check: NeedsCheck | null; costTo: string | null; nameTo: string | null };
    const byName = (a: Row, b: Row) => nameOf(a.m).localeCompare(nameOf(b.m));
    const price = (r: Row) => Number(r.m.price) || 0;
    const toUpdate = (r: Row) => (toUpdateRow(r) ? 0 : 1);
    return (a: Row, b: Row) => {
        let diff = 0;
        if (sort === "number") diff = a.m.item_number.localeCompare(b.m.item_number, undefined, { numeric: true });
        else if (sort === "price-high") diff = price(b) - price(a);
        else if (sort === "price-low") diff = price(a) - price(b);
        else if (sort === "update") diff = toUpdate(a) - toUpdate(b);
        return diff || byName(a, b);
    };
}

/** What's not saved in a group's window, in a sentence. */
function unsavedText(pricing: boolean, tags: boolean) {
    if (pricing && tags) return "Your changes to its pricing and name tags aren't saved yet.";
    return pricing ? "Your changes to its pricing aren't saved yet." : "Your changes to its name tags aren't saved yet.";
}

/** Two sets of a group's name tags read the same (an edit undone is no edit). */
const sameTags = (a: GroupTags, b: GroupTags) => JSON.stringify(sortedTags(a)) === JSON.stringify(sortedTags(b));
const sortedTags = (t: GroupTags) => ({
    choices: mentioned(t.choices),
    includes: mentioned(t.includes),
    lines: mentioned(t.lines),
});
/** A group's tags of one kind it mentions, in order (one not listed, or null, is the same: not mentioned). */
const mentioned = (r: Record<string, string | null>) =>
    Object.entries(r)
        .filter(([, v]) => v !== null)
        .sort(([a], [b]) => a.localeCompare(b));

/**
 * The name tags the group sets for its items, with an example from its first item, edited as a draft (`draft`, kept by the window,
 * like its pricing: the tab marked, closing asks first). In its card, as its Pricing card has its prices: under them the names that
 * would then read differently, each with what changes, and along its bottom its save bar (Save & update, Save only, Cancel). Not
 * being edited: the names that need updating now, and along its bottom Update N names (asked first), or some on its Items tab.
 * Updating, the list stays as it was, each row marked as it's saved and gone once done; any turned down stay, marked.
 */
function GroupNameTags(props: Readonly<{ data: PriceListData; group: PricingGroupRow; draft: GroupTags | null; onDraft: (draft: GroupTags | null) => void; /** Its Items tab. */ onItems: () => void }>) {
    const { data, group, draft } = props;
    const saver = usePriceListSaver();
    const ask = useGroupUpdateConfirm(data);
    // Saving, and then updating its names: how far it's got (one line in its bar).
    const [progress, setProgress] = useState<string | null>(null);
    const [error, setError] = useState<Error | null>(null);
    // Saving and updating: the names as they were when it started (saved, the draft is the group's, so none would read differently).
    const [frozen, setFrozen] = useState<Renamed[] | null>(null);
    const updates = useRowUpdates();
    const members = membersOf(data, group.id).sort((a, b) => itemName(data, a).localeCompare(itemName(data, b)));
    const tags = draft ?? group.tags;
    const edited = draft !== null && !sameTags(draft, group.tags);
    // An edit back to what's saved is no edit: the draft goes.
    const change = (next: GroupTags) => {
        props.onDraft(sameTags(next, group.tags) ? null : next);
        return Promise.resolve();
    };
    // Its items whose names read differently with these tags (being edited: the draft's; else the saved ones: those that need updating now).
    const renamed = members.flatMap((m) => {
        const to = groupTagsName(m.name, tags, data.nameTags, data.itemPricing[m.uuid]?.tagOverrides);
        return to === null ? [] : [{ m, to }];
    });
    const shown = frozen ?? renamed;
    const busy = progress !== null;
    const failed = frozen !== null && !busy && [...updates.rows.values()].some((r) => r.state === "failed");
    const canEdit = can(data, "groups");
    // Updating its items' names to them changes items (the server asks items.edit too): without it, its name tags save only.
    const canNames = canEdit && can(data, "items.edit");
    const stillShown = useStillShown();
    const close = () => {
        setFrozen(null);
        setError(null);
        updates.clear();
        // Another group since (its window moved on): its draft isn't this one's to clear.
        if (stillShown.current) props.onDraft(null);
    };

    // Save the draft, and (`update`) its names one by one (as the person clicking): one turned down doesn't stop the rest.
    const save = async (update: boolean) => {
        if (!draft) return;
        // Many names at once: asked first.
        if (update && !(await ask.names(renamed.length, true))) return;
        setError(null);
        updates.clear();
        if (update) setFrozen(renamed);
        setProgress("Saving...");
        try {
            await saver.updateGroup(group.id, { tags: draft });
        } catch (err) {
            setError(err as Error);
            setFrozen(null);
            setProgress(null);
            return;
        }
        try {
            await updateEach(update ? renamed.map((r) => r.m.uuid) : [], saver.updateNameToGroup, "names", setProgress, updates.track("names"));
            close();
        } catch (err) {
            setError(err as Error);
        }
    };
    // Not being edited: all its names to update, asked first (they change many items at once).
    const updateAll = async () => {
        const uuids = renamed.map((r) => r.m.uuid);
        if (!(await ask.names(uuids.length))) return;
        setError(null);
        updates.clear();
        updateEach(uuids, saver.updateNameToGroup, "names", setProgress, updates.track("names"))
            .catch((err: Error) => setError(err))
            .finally(updates.settle);
    };

    const progressLine = (
        <span className="inline-flex items-center gap-2">
            <Spinner className="size-4" /> {progress}
        </span>
    );
    const n = shown.length;
    // Being edited (or saved and updating): its save bar.
    const saveBar =
        draft !== null && (edited || frozen) ? (
            <DraftBar
                message={
                    busy
                        ? progressLine
                        : failed
                          ? "Its name tags are saved, but some names couldn't be updated: they're marked above."
                          : <span className="font-medium">{n === 1 ? "1 name changes" : `${n || "No"} names change`}</span>
                }
                error={error}
                actions={
                    failed ? (
                        <button type="button" className="btn-secondary" onClick={close}>
                            Close
                        </button>
                    ) : (
                        <>
                            {n && canNames ? (
                                <>
                                    <button type="button" className="btn-primary" disabled={busy} title={`Save the name tags and update the ${itemCount(n, "name")} they change`} onClick={() => void save(true)}>
                                        Save &amp; update
                                    </button>
                                    <button type="button" className="btn-secondary" disabled={busy} title="Save the name tags only: its names stay as they are, and show as to update on its Items tab" onClick={() => void save(false)}>
                                        Save only
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="btn-primary" disabled={busy} onClick={() => void save(false)}>
                                    Save name tags
                                </button>
                            )}
                            <button type="button" className="btn-secondary" disabled={busy} onClick={close}>
                                Cancel
                            </button>
                        </>
                    )
                }
            />
        ) : null;
    // Not being edited, with names to update: what's to update, and Update N names (or some, on its Items tab).
    const updateBar =
        !saveBar && n ? (
            <DraftBar
                message={
                    busy ? (
                        progressLine
                    ) : (
                        <>
                            {n === 1 ? "1 name needs updating." : `${n} names need updating.`}
                            <span className="block text-xs">
                                {canNames ? "Choose which to update on the " : "They're updated from the "}
                                <button type="button" className="link-button text-xs" onClick={props.onItems}>
                                    Items tab
                                </button>
                                .
                            </span>
                        </>
                    )
                }
                error={error}
                actions={
                    canNames ? (
                        <button type="button" className="btn-primary" disabled={busy} title={`Update ${itemCount(n, "name")} to the group's name tags`} onClick={() => void updateAll()}>
                            Update {n === 1 ? "name" : `${n} names`}
                        </button>
                    ) : null
                }
            />
        ) : null;
    return (
        <Box>
            <GroupTagFields
                tags={data.nameTags}
                set={tags}
                exampleBase={members[0]?.name ?? "Example item"}
                disabled={!canEdit || busy || frozen !== null}
                onChange={change}
                after={(saveBar || updateBar) && n ? <RenamedList renamed={shown} updates={updates.rows} /> : null}
                footer={saveBar ?? updateBar}
                plainFooter={!saveBar}
            />
        </Box>
    );
}

type Renamed = { m: MaterialRow; to: string };

/**
 * Names that read differently with a group's tags: each with its change marked, under a heading. Being updated (`updates`, by
 * "names:uuid"): the one being saved has a spinner, those waiting are faded, those done go, and one turned down says so (why, on
 * hover).
 */
function RenamedList({ renamed, updates }: Readonly<{ renamed: readonly Renamed[]; updates: ReadonlyMap<string, RowUpdate> }>) {
    const left = renamed.filter(({ m }) => updates.get(`names:${m.uuid}`)?.state !== "done");
    if (!left.length) return null;
    return (
        <ul className="divide-y divide-line rounded-md border border-line bg-surface">
            <li className="px-3 py-1.5 text-xs font-medium text-muted">Name</li>
            {left.map(({ m, to }) => {
                const state = updates.get(`names:${m.uuid}`);
                return (
                    <li key={m.uuid} className={clsx("flex items-start gap-2 px-3 py-1.5 text-xs text-muted", state?.state === "waiting" && "opacity-50")}>
                        {state?.state === "updating" ? <Spinner className="mt-0.5 size-3.5 shrink-0" /> : null}
                        <span className="min-w-0 flex-1">
                            <NameChange from={m.name} to={to} />
                        </span>
                        {state?.state === "failed" ? (
                            <span className="shrink-0 text-danger" title={state.error}>
                                Couldn&apos;t update
                            </span>
                        ) : null}
                    </li>
                );
            })}
        </ul>
    );
}
